import React from 'react';

interface StatCardProps {
  label: React.ReactNode;
  value: string | number;
  subtext?: string;
  /** A period-over-period chip, drawn beside the value. */
  delta?: React.ReactNode;
  /** Nothing collects this number yet. Says so instead of showing a value or a loading shimmer. */
  untracked?: boolean;
  icon: React.ReactNode;
  accent?: 'violet' | 'blue' | 'green' | 'pink' | 'amber' | 'cyan';
  pending?: boolean;
  // 'default' keeps the existing candy-colored card chrome (MonitoringTab, MapPage).
  // 'dense' applies an enterprise data-dashboard treatment: uniform neutral
  // border, tighter spacing, restrained typography, tabular numerals. Accent
  // color is kept only on the icon glyph so data leads the visual hierarchy.
  variant?: 'default' | 'dense';
}

const accentMap: Record<string, { card: string; icon: string; value: string; iconDense: string }> = {
  violet: { card: 'border-violet-800/30', icon: 'bg-violet-950/60 border-violet-700/40 text-violet-400', value: 'text-violet-100', iconDense: 'text-violet-400' },
  blue:   { card: 'border-blue-800/30',   icon: 'bg-blue-950/60 border-blue-700/40 text-blue-400',     value: 'text-blue-100',   iconDense: 'text-blue-400'   },
  green:  { card: 'border-green-800/30',  icon: 'bg-green-950/60 border-green-700/40 text-green-400',  value: 'text-green-100',  iconDense: 'text-green-400'  },
  pink:   { card: 'border-pink-800/30',   icon: 'bg-pink-950/60 border-pink-700/40 text-pink-400',     value: 'text-pink-100',   iconDense: 'text-pink-400'   },
  amber:  { card: 'border-amber-800/30',  icon: 'bg-amber-950/60 border-amber-700/40 text-amber-400',  value: 'text-amber-100',  iconDense: 'text-amber-400'  },
  cyan:   { card: 'border-cyan-800/30',   icon: 'bg-cyan-950/60 border-cyan-700/40 text-cyan-400',     value: 'text-cyan-100',   iconDense: 'text-cyan-400'   },
};

function StatCard({
  label,
  value,
  subtext,
  icon,
  accent = 'violet',
  pending = false,
  variant = 'default',
  delta,
  untracked = false,
}: StatCardProps): React.ReactElement {
  const c = accentMap[accent];
  const dense = variant === 'dense';
  const notTracked = <p className="text-sm font-medium text-gray-500 italic py-1">Not tracked yet</p>;

  if (dense) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-start gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg bg-gray-800/60 flex items-center justify-center ${c.iconDense}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
          {untracked ? (
            notTracked
          ) : pending ? (
            <div className="h-7 w-20 bg-gray-800 rounded animate-pulse" />
          ) : (
            <p className="text-2xl font-semibold text-white tabular-nums tracking-tight flex items-center gap-2 flex-wrap">
              {value}
              {delta}
            </p>
          )}
          {subtext && (untracked || !pending) && (
            <p className="text-[11px] text-gray-500 mt-1 tabular-nums">{subtext}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-900 border ${c.card} rounded-2xl p-5 flex items-start gap-4`}>
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center ${c.icon}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
        {untracked ? (
          notTracked
        ) : pending ? (
          <div className="h-7 w-20 bg-gray-800 rounded animate-pulse" />
        ) : (
          <p className={`text-2xl font-bold ${c.value} flex items-center gap-2 flex-wrap`}>
            {value}
            {delta}
          </p>
        )}
        {subtext && (untracked || !pending) && (
          <p className="text-xs text-gray-600 mt-1">{subtext}</p>
        )}
      </div>
    </div>
  );
}

export default StatCard;
